import { Button } from '@/components/ui/button';
import { Mail, MessageSquare } from 'lucide-react';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Page, PageHeader } from '@/components/layout/page';
import { DevNote } from '@/components/ui/dev-note';
import { HoneypotField, Input, Textarea } from '@/components/ui/field';
import { Alert, Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';

/**
 * Formulaire de contact — `POST /support/contact`.
 *
 * Les messages alimentent la boîte unifiée du site côté back-office : il n'y a
 * pas de serveur d'emails à monter, ni de service tiers à brancher.
 *
 * Protection anti-spam : l'API applique sa propre limitation de débit, et ce
 * formulaire ajoute un **champ pot de miel** invisible (voir `HoneypotField`).
 * S'il est rempli, c'est un robot : on affiche la confirmation habituelle sans
 * rien envoyer. Répondre « message envoyé » à un robot vaut mieux que de lui
 * signaler qu'il a été repéré.
 */
export default function ContactPage() {
    const site = useSite();
    const { user } = useSession();

    const [name, setName] = useState('');
    const [email, setEmail] = useState(user?.email ?? '');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [honeypot, setHoneypot] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();

        // Pot de miel rempli : on simule le succès sans appeler l'API.
        if (honeypot.trim() !== '') {
            setSent(true);

            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await dashlite.contactSupport({
                ...(name.trim() ? { name: name.trim() } : {}),
                email: email.trim(),
                subject: subject.trim(),
                message: message.trim(),
            });

            setSent(true);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Nous contacter">
            <PageHeader
                eyebrow="Assistance"
                title="Nous contacter"
                description="Une question sur une fiche, un accès ou une facture ? Écrivez-nous, nous répondons sous un jour ouvré."
            />

            <Section>
                <Container className="grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]">
                    <Card>
                        <CardBody>
                            {sent ? (
                                <Alert tone="positive" title="Message envoyé">
                                    Merci, votre message est bien arrivé. Nous vous répondons à l'adresse indiquée.
                                    <div className="mt-3">
                                        <Button variant="secondary" size="sm" onClick={() => setSent(false)}>
                                            Écrire un autre message
                                        </Button>
                                    </div>
                                </Alert>
                            ) : (
                                <form onSubmit={handleSubmit} className="relative space-y-5" noValidate>
                                    <HoneypotField value={honeypot} onChange={setHoneypot} />

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Input
                                            label="Nom"
                                            optional
                                            autoComplete="name"
                                            value={name}
                                            error={fields.name}
                                            onChange={(event) => setName(event.target.value)}
                                        />

                                        <Input
                                            data-dashlite="contact-email"
                                            label="Adresse email"
                                            type="email"
                                            required
                                            autoComplete="email"
                                            value={email}
                                            error={fields.email}
                                            onChange={(event) => setEmail(event.target.value)}
                                        />
                                    </div>

                                    <Input
                                        data-dashlite="contact-subject"
                                        label="Objet"
                                        required
                                        value={subject}
                                        error={fields.subject}
                                        onChange={(event) => setSubject(event.target.value)}
                                        placeholder="Question sur mon accès"
                                    />

                                    <Textarea
                                        data-dashlite="contact-message"
                                        label="Message"
                                        required
                                        value={message}
                                        error={fields.message}
                                        onChange={(event) => setMessage(event.target.value)}
                                        placeholder="Décrivez votre demande le plus précisément possible."
                                    />

                                    {error !== null && Object.keys(fields).length === 0 && (
                                        <Alert tone="critical" title={presentError(error).message}>
                                            {presentError(error).hint}
                                        </Alert>
                                    )}

                                    <Button data-dashlite="contact-submit" type="submit" size="lg" loading={submitting}>
                                        <MessageSquare className="size-4" aria-hidden />
                                        Envoyer le message
                                    </Button>
                                </form>
                            )}
                        </CardBody>
                    </Card>

                    <div className="space-y-6">
                        {site.contact_email && (
                            <Card>
                                <CardBody className="space-y-2">
                                    <h2 className="text-ink font-sans text-sm font-semibold">Par email</h2>
                                    <a
                                        href={`mailto:${site.contact_email}`}
                                        className="text-brand-700 inline-flex items-center gap-2 text-sm hover:underline"
                                    >
                                        <Mail className="size-4" aria-hidden />
                                        {site.contact_email}
                                    </a>
                                    <p className="text-ink-muted text-xs">
                                        Cette adresse vient de <code className="font-mono">GET /site</code> : elle est
                                        configurée site par site, jamais codée en dur dans le front.
                                    </p>
                                </CardBody>
                            </Card>
                        )}

                        <DevNote title="Ce que fait cet écran" endpoints={['POST /support/contact']}>
                            <p>
                                Les messages alimentent la boîte unifiée du back-office. L'anti-spam repose sur la
                                limitation de débit de l'API et sur un champ pot de miel invisible côté front.
                            </p>
                            <p>
                                La route est publique : un visiteur non connecté peut écrire. Un token présent est
                                simplement rattaché au message.
                            </p>
                        </DevNote>
                    </div>
                </Container>
            </Section>
        </Page>
    );
}
